import React, { useEffect, useMemo, useState } from 'react';
import { ClipboardListIcon, SearchIcon, UserIcon, ClockIcon, ShieldAlertIcon, DownloadIcon, ArchiveIcon, PowerOffIcon, CheckCircleIcon, RotateCcwIcon, XCircleIcon, LockIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Badge } from '../ui/Badge';
import { Pagination } from '../ui/Pagination';
import { Modal } from '../ui/Modal';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { usePagination } from '../../hooks/usePagination';
import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';
import { exportAuditLogsToExcel } from '../../utils/excelExport';
import { exportAuditLogsToPdf } from '../../utils/pdfExport';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
// ============ SAFE HELPERS ============
function toStr(v: any): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (v instanceof Date) {
    try {
      return v.toISOString();
    } catch {
      return '';
    }
  }
  try {
    return JSON.stringify(v);
  } catch {
    return '';
  }
}
function fmtDate(date: any): string {
  try {
    if (!date) return 'Sin fecha';
    const d = date instanceof Date ? date : new Date(typeof date === 'string' || typeof date === 'number' ? date : Date.now());
    if (isNaN(d.getTime())) return 'Fecha inválida';
    return d.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }) + ' • ' + d.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return 'Sin fecha';
  }
}
const MODULE_NAMES: Record<string, string> = {
  patients: 'Pacientes',
  doctors: 'Doctores',
  appointments: 'Citas',
  services: 'Servicios',
  insurance: 'Seguros',
  insurances: 'Seguros',
  specialties: 'Especialidades',
  contacts: 'Contactos',
  companies: 'Empresas',
  opportunities: 'Oportunidades',
  clinicalHistory: 'Historial Clínico',
  clinical_histories: 'Historial Clínico',
  followUpNotes: 'Notas de Seguimiento',
  follow_up_notes: 'Notas de Seguimiento',
  auditLog: 'Bitácora',
  audit_logs: 'Bitácora',
  settings: 'Configuración',
  dashboard: 'Panel de Control',
  roles: 'Roles',
  permissions: 'Permisos',
  products: 'Productos',
  product_sales: 'Ventas de Productos'
};
function moduleName(m: string): string {
  return MODULE_NAMES[m] || (m ? m.charAt(0).toUpperCase() + m.slice(1) : 'Módulo');
}
// ============ TYPES ============
interface LogItem {
  id: string;
  ts: Date;
  userId: string;
  userName: string;
  action: string;
  module: string;
  entityId: string;
  entityName: string;
  details: string;
}
function parseLog(raw: any): LogItem | null {
  try {
    if (!raw || typeof raw !== 'object') return null;
    return {
      id: toStr(raw.id) || String(Math.random()),
      ts: raw.timestamp instanceof Date ? raw.timestamp : new Date(typeof raw.timestamp === 'string' || typeof raw.timestamp === 'number' ? raw.timestamp : Date.now()),
      userId: toStr(raw.userId),
      userName: toStr(raw.userName),
      action: toStr(raw.action),
      module: toStr(raw.module),
      entityId: toStr(raw.entityId),
      entityName: toStr(raw.entityName),
      details: toStr(raw.details)
    };
  } catch {
    return null;
  }
}
// ============ SUB-COMPONENTS (inline to avoid import issues) ============
function ActionBadge({
  action
}: {
  action: string;
}) {
  switch (action) {
    case 'create':
      return <Badge variant="success" data-id="element-496">{'Creación'}</Badge>;
    case 'update':
      return <Badge variant="info" data-id="element-497">{'Edición'}</Badge>;
    case 'delete':
      return <Badge variant="danger" data-id="element-498">{'Eliminación'}</Badge>;
    case 'complete':
      return <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400" data-id="element-499">
          {'Completado'}
        </Badge>;
    case 'cancel':
      return <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400" data-id="element-500">
          {'Cancelado'}
        </Badge>;
    case 'reschedule':
      return <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400" data-id="element-501">
          {'Reprogramado'}
        </Badge>;
    default:
      return <Badge variant="default" data-id="element-502">{action || 'Acción'}</Badge>;
  }
}
function LogRow({
  log,
  viewMode,
  onArchive,
  onDeactivate,
  onRestore,
  onClick
}: {
  log: LogItem;
  viewMode: string;
  onArchive: (id: string) => void;
  onDeactivate: (id: string) => void;
  onRestore: (id: string) => void;
  onClick: () => void;
}) {
  return <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group cursor-pointer" onClick={onClick} data-id="element-503">
      <td className="py-4 px-6 whitespace-nowrap" data-id="element-504">
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300" data-id="element-505">
          <ClockIcon className="w-4 h-4 text-gray-400" data-id="element-506" />
          <span data-id="element-507">{fmtDate(log.ts)}</span>
        </div>
      </td>
      <td className="py-4 px-6 whitespace-nowrap" data-id="element-508">
        <div className="flex items-center gap-2" data-id="element-509">
          <div className="w-8 h-8 rounded-full bg-sky-100 dark:bg-sky-900 flex items-center justify-center text-sky-600 dark:text-sky-400" data-id="element-510">
            <UserIcon className="w-4 h-4" data-id="element-511" />
          </div>
          <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-512">
            {log.userName}
          </span>
        </div>
      </td>
      <td className="py-4 px-6 whitespace-nowrap" data-id="element-513">
        <ActionBadge action={log.action} data-id="element-514" />
      </td>
      <td className="py-4 px-6 whitespace-nowrap hidden sm:table-cell" data-id="element-515">
        <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-516">
          {moduleName(log.module)}
        </span>
      </td>
      <td className="py-4 px-6 hidden sm:table-cell" data-id="element-517">
        <p className="text-sm text-gray-900 dark:text-gray-200" data-id="element-518">
          {log.details}
        </p>
        {log.entityName !== '' && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-519">
            {'Entidad: '}
            {log.entityName}
          </p>}
      </td>
      <td className="py-4 px-6" data-id="element-520">
        <div className="flex items-center justify-end gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity" data-id="element-521">
          {viewMode === 'active' ? <>
              <button onClick={e => {
            e.stopPropagation();
            onArchive(log.id);
          }} className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Archivar" data-id="element-522">
                <ArchiveIcon className="w-4 h-4" data-id="element-523" />
              </button>
              <button onClick={e => {
            e.stopPropagation();
            onDeactivate(log.id);
          }} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Desactivar" data-id="element-524">
                <PowerOffIcon className="w-4 h-4" data-id="element-525" />
              </button>
            </> : <button onClick={e => {
          e.stopPropagation();
          onRestore(log.id);
        }} className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Restaurar" data-id="element-526">
              <RotateCcwIcon className="w-4 h-4" data-id="element-527" />
            </button>}
        </div>
      </td>
    </tr>;
}
// ============ ENTITY CONTEXT (shows related entity details in modal) ============
function EntityContext({
  log
}: {
  log: LogItem;
}) {
  const {
    appointments,
    patients,
    doctors,
    services,
    getPatient,
    getDoctor,
    getService
  } = useData();
  if (!log.entityId) return null;
  const mod = log.module;
  // Appointments
  if (mod === 'appointments') {
    const apt = appointments.find(a => a.id === log.entityId);
    if (!apt) return null;
    const patient = getPatient(apt.patientId);
    const doctor = getDoctor(apt.doctorId);
    const serviceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId].filter(Boolean);
    const serviceNames = serviceIds.map(sId => getService(sId)?.name).filter(Boolean);
    return <div className="bg-sky-50 dark:bg-sky-900/20 rounded-xl p-4 border border-sky-100 dark:border-sky-800/30" data-id="element-528">
        <p className="text-xs font-medium text-sky-600 dark:text-sky-400 uppercase tracking-wider mb-3" data-id="element-529">
          Información de la Cita
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-530">
          <div data-id="element-531">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-532">Paciente</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-533">
              {patient?.name || apt.patientId}
            </p>
          </div>
          <div data-id="element-534">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-535">Doctor</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-536">
              {doctor?.name || 'Sin asignar'}
            </p>
          </div>
          <div data-id="element-537">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-538">
              Fecha de la Cita
            </p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-539">
              {apt.date}
            </p>
          </div>
          <div data-id="element-540">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-541">Hora</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-542">
              {apt.time || 'N/A'}
            </p>
          </div>
          {serviceNames.length > 0 && <div className="sm:col-span-2" data-id="element-543">
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-544">
                Servicio(s)
              </p>
              <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-545">
                {serviceNames.join(', ')}
              </p>
            </div>}
          <div data-id="element-546">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-547">Estado</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white capitalize" data-id="element-548">
              {apt.status}
            </p>
          </div>
          {apt.notes && <div className="sm:col-span-2" data-id="element-549">
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-550">Notas</p>
              <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-551">
                {apt.notes}
              </p>
            </div>}
        </div>
      </div>;
  }
  // Patients
  if (mod === 'patients') {
    const patient = patients.find(p => p.id === log.entityId);
    if (!patient) return null;
    return <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4 border border-emerald-100 dark:border-emerald-800/30" data-id="element-552">
        <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mb-3" data-id="element-553">
          Información del Paciente
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-554">
          <div data-id="element-555">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-556">Nombre</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-557">
              {patient.name}
            </p>
          </div>
          <div data-id="element-558">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-559">Teléfono</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-560">
              {patient.phone || 'N/A'}
            </p>
          </div>
          <div data-id="element-561">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-562">Email</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-563">
              {patient.email || 'N/A'}
            </p>
          </div>
          <div data-id="element-564">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-565">Género</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white capitalize" data-id="element-566">
              {patient.gender || 'N/A'}
            </p>
          </div>
        </div>
      </div>;
  }
  // Doctors
  if (mod === 'doctors') {
    const doctor = doctors.find(d => d.id === log.entityId);
    if (!doctor) return null;
    return <div className="bg-violet-50 dark:bg-violet-900/20 rounded-xl p-4 border border-violet-100 dark:border-violet-800/30" data-id="element-567">
        <p className="text-xs font-medium text-violet-600 dark:text-violet-400 uppercase tracking-wider mb-3" data-id="element-568">
          Información del Doctor
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-569">
          <div data-id="element-570">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-571">Nombre</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-572">
              {doctor.name}
            </p>
          </div>
          <div data-id="element-573">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-574">Teléfono</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-575">
              {doctor.phone || 'N/A'}
            </p>
          </div>
          <div data-id="element-576">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-577">Email</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-578">
              {doctor.email || 'N/A'}
            </p>
          </div>
        </div>
      </div>;
  }
  // Services
  if (mod === 'services') {
    const service = services.find(s => s.id === log.entityId);
    if (!service) return null;
    return <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-100 dark:border-amber-800/30" data-id="element-579">
        <p className="text-xs font-medium text-amber-600 dark:text-amber-400 uppercase tracking-wider mb-3" data-id="element-580">
          Información del Servicio
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-581">
          <div data-id="element-582">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-583">Nombre</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-584">
              {service.name}
            </p>
          </div>
          <div data-id="element-585">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-586">Duración</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-587">
              {service.duration} min
            </p>
          </div>
          <div data-id="element-588">
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-589">Precio</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-590">
              RD$ {service.price.toLocaleString()}
            </p>
          </div>
        </div>
      </div>;
  }
  return null;
}
// ============ MAIN COMPONENT ============
export function AuditLog() {
  const dataCtx = useData();
  const {
    user
  } = useAuth();
  const permCtx = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterModule, setFilterModule] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  const [itemStatuses, setItemStatuses] = useState<Record<string, string>>({});
  const [viewMode, setViewMode] = useState('active');
  const [confirmAction, setConfirmAction] = useState<{
    id: string;
    type: string;
  } | null>(null);
  const [selectedLog, setSelectedLog] = useState<LogItem | null>(null);
  const readOnly = permCtx.isReadOnly('auditLog');
  const isAdmin = user?.role === 'administrador';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canExportExcel = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canExportPdf = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  // Load statuses from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('savidna-audit-log-status');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          setItemStatuses(parsed);
        }
      }
    } catch {
      /* ignore */
    }
  }, []);
  // Save statuses to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('savidna-audit-log-status', JSON.stringify(itemStatuses));
    } catch {
      /* ignore */
    }
  }, [itemStatuses]);
  // Parse all logs once
  const allLogs = useMemo<LogItem[]>(() => {
    const result: LogItem[] = [];
    const raw = dataCtx.auditLogs;
    if (!Array.isArray(raw)) return result;
    for (let i = 0; i < raw.length; i++) {
      const parsed = parseLog(raw[i]);
      if (parsed) result.push(parsed);
    }
    return result;
  }, [dataCtx.auditLogs]);
  const getStatus = (id: string): string => itemStatuses[id] || 'active';
  // Filter logs
  const filteredLogs = useMemo<LogItem[]>(() => {
    const search = (searchTerm || '').toLowerCase();
    return allLogs.filter(log => {
      if (getStatus(log.id) !== viewMode) return false;
      if (filterModule && log.module !== filterModule) return false;
      if (filterAction && log.action !== filterAction) return false;
      if (search) {
        const inDetails = log.details.toLowerCase().includes(search);
        const inUser = log.userName.toLowerCase().includes(search);
        const inEntity = log.entityName.toLowerCase().includes(search);
        if (!inDetails && !inUser && !inEntity) return false;
      }
      return true;
    });
  }, [allLogs, searchTerm, filterModule, filterAction, viewMode, itemStatuses]);
  const {
    sortedItems: sortedLogs,
    requestSort: logRequestSort,
    isSorted: logIsSorted
  } = useSortableData(filteredLogs, {
    key: 'ts',
    direction: 'desc'
  });
  const pagination = usePagination(sortedLogs, {
    itemsPerPage: 10
  });
  // Counts
  const activeCount = useMemo(() => allLogs.filter(l => getStatus(l.id) === 'active').length, [allLogs, itemStatuses]);
  const archivedCount = useMemo(() => allLogs.filter(l => getStatus(l.id) === 'archived').length, [allLogs, itemStatuses]);
  const deactivatedCount = useMemo(() => allLogs.filter(l => getStatus(l.id) === 'deactivated').length, [allLogs, itemStatuses]);
  // Module options for filter
  const moduleOptions = useMemo(() => {
    const set = new Set<string>();
    allLogs.forEach(l => {
      if (l.module) set.add(l.module);
    });
    return Array.from(set).map(m => ({
      value: m,
      label: moduleName(m)
    }));
  }, [allLogs]);
  const actionOptions: {
    value: string;
    label: string;
  }[] = [{
    value: 'create',
    label: 'Creación'
  }, {
    value: 'update',
    label: 'Actualización'
  }, {
    value: 'delete',
    label: 'Eliminación'
  }, {
    value: 'complete',
    label: 'Completado'
  }, {
    value: 'cancel',
    label: 'Cancelación'
  }, {
    value: 'reschedule',
    label: 'Reprogramación'
  }];
  // Actions
  const handleArchive = (id: string) => {
    setItemStatuses(p => ({
      ...p,
      [id]: 'archived'
    }));
    setConfirmAction(null);
  };
  const handleDeactivate = (id: string) => {
    setItemStatuses(p => ({
      ...p,
      [id]: 'deactivated'
    }));
    setConfirmAction(null);
  };
  const handleRestore = (id: string) => {
    setItemStatuses(p => {
      const n = {
        ...p
      };
      delete n[id];
      return n;
    });
  };
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    try {
      await new Promise(r => setTimeout(r, 200));
      let dataToExport = dataCtx.auditLogs as any;
      if (dateFrom && dateTo) {
        dataToExport = (dataCtx.auditLogs as any[]).filter((log: any) => {
          const dateStr = new Date(log.timestamp).toISOString().split('T')[0];
          return dateStr >= dateFrom && dateStr <= dateTo;
        });
      }
      exportAuditLogsToExcel(dataToExport);
    } catch (e) {
      console.error(e);
    }
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = dataCtx.auditLogs as any[];
    if (dateFrom && dateTo) {
      dataToExport = (dataCtx.auditLogs as any[]).filter((log: any) => {
        const dateStr = new Date(log.timestamp).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportAuditLogsToPdf(dataToExport);
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  // Permission check
  if (!permCtx.canView('auditLog')) {
    return <div className="flex flex-col items-center justify-center h-[60vh] text-center p-6" data-id="element-591">
        <ShieldAlertIcon className="w-16 h-16 text-gray-300 mb-4" data-id="element-592" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2" data-id="element-593">
          {'Acceso Restringido'}
        </h2>
        <p className="text-gray-500 dark:text-gray-400 max-w-md" data-id="element-594">
          {'No tienes permisos para ver la bitácora de auditoría.'}
        </p>
      </div>;
  }
  const items = pagination.paginatedItems;
  return <div className="space-y-6" data-id="element-595">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-600 via-purple-500 to-indigo-500 p-6 text-white shadow-xl" data-id="element-596">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-597" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full" data-id="element-598">
          <div data-id="element-599">
            <div className="flex items-center gap-3 mb-2" data-id="element-600">
              <div className="p-2.5 bg-white/15 backdrop-blur-sm rounded-xl" data-id="element-601">
                <ClipboardListIcon className="w-6 h-6" data-id="element-602" />
              </div>
              <h1 className="text-2xl font-bold" data-id="element-603">{'Bitácora de Auditoría'}</h1>
            </div>
            <p className="text-purple-100 max-w-xl" data-id="element-604">
              {'Registro de actividades y cambios en el sistema'}
              {readOnly ? <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-605">
                  {'Solo lectura'}
                </span> : null}
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-606">
            {canExportPdf && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-608" />} onClick={() => setIsPdfExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-607">
                Exportar PDF
              </Button>}
            {canExportExcel && filteredLogs.length > 0 ? <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-610" />} onClick={() => setIsExportModalOpen(true)} isLoading={isExporting} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-609">
                Exportar Excel
              </Button> : null}
          </div>
        </div>
      </div>

      {readOnly ? <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-611">
          <LockIcon className="w-4 h-4" data-id="element-612" />
          <span data-id="element-613">
            {'Solo tienes permisos de lectura. No puedes modificar los registros de auditoría.'}
          </span>
        </div> : null}

      {/* Filters */}
      <Card data-id="element-614">
        <div className="flex flex-col lg:flex-row gap-4" data-id="element-615">
          <div className="flex-1 relative" data-id="element-616">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-617" />
            <Input placeholder="Buscar por usuario, entidad o detalles..." value={searchTerm} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)} className="pl-10" data-id="element-618" />
          </div>
          <div className="w-full lg:w-48" data-id="element-619">
            <Select options={[{
            value: '',
            label: 'Todos los módulos'
          }, ...moduleOptions]} value={filterModule} onChange={setFilterModule} placeholder="Módulo" data-id="element-620" />
          </div>
          <div className="w-full lg:w-48" data-id="element-621">
            <Select options={[{
            value: '',
            label: 'Todas las acciones'
          }, ...actionOptions]} value={filterAction} onChange={setFilterAction} placeholder="Acción" data-id="element-622" />
          </div>
        </div>
      </Card>

      {/* View Mode Tabs */}
      <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full md:w-auto overflow-x-auto" data-id="element-623">
        <button onClick={() => setViewMode('active')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === 'active' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-624">
          <CheckCircleIcon className="w-4 h-4" data-id="element-625" />
          {'Activos'}
          <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-626">
            {String(activeCount)}
          </span>
        </button>
        <button onClick={() => setViewMode('archived')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === 'archived' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-627">
          <ArchiveIcon className="w-4 h-4" data-id="element-628" />
          {'Archivados'}
          <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-629">
            {String(archivedCount)}
          </span>
        </button>
        <button onClick={() => setViewMode('deactivated')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === 'deactivated' ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-630">
          <XCircleIcon className="w-4 h-4" data-id="element-631" />
          {'Desactivados'}
          <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-632">
            {String(deactivatedCount)}
          </span>
        </button>
      </div>

      {/* Table */}
      <Card padding="none" className="overflow-hidden border-0 shadow-lg" data-id="element-633">
        <div className="h-1 bg-gradient-to-r from-purple-500 to-indigo-500" data-id="element-634" />
        {/* ── MOBILE CARDS ── */}
        <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-635">
          {items.length === 0 ? <div className="py-10 text-center text-gray-500 dark:text-gray-400" data-id="element-636">
              <p className="text-sm" data-id="element-637">No se encontraron registros</p>
            </div> : items.map((log, idx) => <div key={log.id + '-m-' + String(idx)} onClick={() => setSelectedLog(log)} className="px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-638">
                <div className="flex items-start justify-between gap-2" data-id="element-639">
                  <div className="flex-1 min-w-0" data-id="element-640">
                    <div className="flex items-center gap-2 flex-wrap" data-id="element-641">
                      <ActionBadge action={log.action} data-id="element-642" />
                      <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-643">
                        {fmtDate(log.ts)}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white mt-1 truncate" data-id="element-644">
                      {log.userName}
                    </p>
                    {log.entityName && <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-645">
                        {log.entityName}
                      </p>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0" data-id="element-646">
                    {viewMode === 'active' && <button onClick={e => {
                e.stopPropagation();
                setConfirmAction({
                  id: log.id,
                  type: 'archive'
                });
              }} className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" data-id="element-647">
                        <ArchiveIcon className="w-4 h-4" data-id="element-648" />
                      </button>}
                    {viewMode !== 'active' && <button onClick={e => {
                e.stopPropagation();
                handleRestore(log.id);
              }} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors" data-id="element-649">
                        <RotateCcwIcon className="w-4 h-4" data-id="element-650" />
                      </button>}
                  </div>
                </div>
              </div>)}
        </div>

        {/* ── DESKTOP TABLE ── */}
        <div className="hidden sm:block overflow-x-auto" data-id="element-651">
          <table className="w-full" data-id="element-652">
            <thead data-id="element-653">
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50" data-id="element-654">
                <SortableHeader label="Fecha y Hora" sortKey="ts" currentDirection={logIsSorted('ts')} onSort={logRequestSort} data-id="element-655" />
                <SortableHeader label="Usuario" sortKey="userName" currentDirection={logIsSorted('userName')} onSort={logRequestSort} data-id="element-656" />
                <SortableHeader label="Acción" sortKey="action" currentDirection={logIsSorted('action')} onSort={logRequestSort} data-id="element-657" />
                <SortableHeader label="Módulo" sortKey="module" currentDirection={logIsSorted('module')} onSort={logRequestSort} className="hidden sm:table-cell" data-id="element-658" />
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden sm:table-cell" data-id="element-659">
                  {'Detalles'}
                </th>
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-660">
                  {'Acciones'}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-661">
              {items.length === 0 ? <tr data-id="element-662">
                  <td colSpan={6} className="py-16 text-center text-gray-500 dark:text-gray-400" data-id="element-663">
                    <div className="flex flex-col items-center justify-center" data-id="element-664">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-665">
                        <ClipboardListIcon className="w-8 h-8 text-gray-400" data-id="element-666" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-667">
                        {'No se encontraron registros'}
                      </h3>
                      <p className="text-sm" data-id="element-668">
                        {searchTerm ? 'Intenta con otros términos de búsqueda' : viewMode === 'active' ? 'La bitácora está vacía' : 'No hay registros en esta vista'}
                      </p>
                    </div>
                  </td>
                </tr> : items.map((log, idx) => <LogRow key={log.id + '-' + String(idx)} log={log} viewMode={viewMode} onClick={() => setSelectedLog(log)} onArchive={id => setConfirmAction({
              id,
              type: 'archive'
            })} onDeactivate={id => setConfirmAction({
              id,
              type: 'deactivate'
            })} onRestore={handleRestore} data-id="element-669" />)}
            </tbody>
          </table>
        </div>
        {/* Shared pagination */}
        {pagination.totalPages > 1 && <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700 pt-4" data-id="element-670">
            <Pagination currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalItems={pagination.totalItems} startIndex={pagination.startIndex} endIndex={pagination.endIndex} onPageChange={pagination.goToPage} onNext={pagination.nextPage} onPrev={pagination.prevPage} data-id="element-671" />
          </div>}
      </Card>

      {/* Confirm Modal */}
      {confirmAction !== null ? <Modal isOpen={true} onClose={() => setConfirmAction(null)} title={confirmAction.type === 'archive' ? 'Archivar Registro' : 'Desactivar Registro'} size="sm" data-id="element-672">
          <div className="space-y-4" data-id="element-673">
            <p className="text-gray-600 dark:text-gray-400" data-id="element-674">
              {confirmAction.type === 'archive' ? '¿Estás seguro de que deseas archivar este registro? Podrás restaurarlo más tarde.' : '¿Estás seguro de que deseas desactivar este registro? No aparecerá en las listas activas.'}
            </p>
            <div className="flex justify-end gap-3" data-id="element-675">
              <Button variant="outline" onClick={() => setConfirmAction(null)} data-id="element-676">
                {'Cancelar'}
              </Button>
              <Button variant="primary" onClick={() => {
            if (confirmAction.type === 'archive') handleArchive(confirmAction.id);else handleDeactivate(confirmAction.id);
          }} data-id="element-677">
                {confirmAction.type === 'archive' ? 'Archivar' : 'Desactivar'}
              </Button>
            </div>
          </div>
        </Modal> : null}

      {/* Detail Modal */}
      {selectedLog !== null ? <Modal isOpen={true} onClose={() => setSelectedLog(null)} title="Detalle del Registro" size="md" icon={<ClipboardListIcon className="w-5 h-5" data-id="element-679" />} accentColor="purple" data-id="element-678">
          <div className="space-y-5" data-id="element-680">
            {/* Action & Status */}
            <div className="flex items-center gap-3" data-id="element-681">
              <ActionBadge action={selectedLog.action} data-id="element-682" />
              <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-683">
                en {moduleName(selectedLog.module)}
              </span>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-684">
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-100 dark:border-gray-700" data-id="element-685">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1" data-id="element-686">
                  Fecha y Hora
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-687">
                  <ClockIcon className="w-4 h-4 text-purple-500" data-id="element-688" />
                  {fmtDate(selectedLog.ts)}
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-100 dark:border-gray-700" data-id="element-689">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1" data-id="element-690">
                  Usuario
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-691">
                  <UserIcon className="w-4 h-4 text-sky-500" data-id="element-692" />
                  {selectedLog.userName || 'Sin usuario'}
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-100 dark:border-gray-700" data-id="element-693">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1" data-id="element-694">
                  Módulo
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-695">
                  {moduleName(selectedLog.module)}
                </p>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-100 dark:border-gray-700" data-id="element-696">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1" data-id="element-697">
                  ID de Entidad
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white font-mono text-xs break-all" data-id="element-698">
                  {selectedLog.entityId || 'N/A'}
                </p>
              </div>
            </div>

            {/* Entity Name */}
            {selectedLog.entityName && <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-100 dark:border-gray-700" data-id="element-699">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1" data-id="element-700">
                  Entidad
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-701">
                  {selectedLog.entityName}
                </p>
              </div>}

            {/* Details */}
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 border border-purple-100 dark:border-purple-800/30" data-id="element-702">
              <p className="text-xs font-medium text-purple-600 dark:text-purple-400 uppercase tracking-wider mb-2" data-id="element-703">
                Detalles de la Acción
              </p>
              <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap" data-id="element-704">
                {selectedLog.details || 'Sin detalles adicionales.'}
              </p>
            </div>

            {/* Entity Context - lookup related data */}
            <EntityContext log={selectedLog} data-id="element-705" />

            {/* Close button */}
            <div className="flex justify-end pt-2" data-id="element-706">
              <Button variant="outline" onClick={() => setSelectedLog(null)} data-id="element-707">
                Cerrar
              </Button>
            </div>
          </div>
        </Modal> : null}

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Bitácora" isExporting={isExporting} data-id="element-708" />

      {/* PDF Export Modal */}
      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Bitácora a PDF" isExporting={isPdfExporting} data-id="element-709" />
    </div>;
}